/**
 * Mensagens de conclusão de jobs de exclusão (contas/revendedores).
 */

const recentlyHandledDeletionJobs = new Set()

export function markDeletionJobHandled(jobId) {
  if (!jobId) return
  recentlyHandledDeletionJobs.add(String(jobId))
  setTimeout(() => {
    recentlyHandledDeletionJobs.delete(String(jobId))
  }, 120000)
}

export function isDeletionJobAlreadyHandled(jobId) {
  return Boolean(jobId && recentlyHandledDeletionJobs.has(String(jobId)))
}

function summarizeDeletionResult(status) {
  const result = status?.result || {}
  const deleted = Number(result.deleted ?? result.processed ?? 0)
  const total = Number(result.total ?? deleted)
  const failedServers = Array.isArray(result.sync_results?.failed) ? result.sync_results.failed : []

  return {
    deleted,
    total,
    failedServers,
    message: status?.message || '',
    syncWarnings: Boolean(result.sync_warnings),
  }
}

function formatServerSyncDetails(failedServers, t, maxItems = 5) {
  if (!failedServers.length) return ''

  const lines = failedServers.slice(0, maxItems).map((entry) => {
    const server = entry.server || '?'
    const type = entry.type || '?'
    const error = String(entry.error || t('deletionJobs.syncUnknownError')).slice(0, 160)
    return `• ${server} (${type}): ${error}`
  })

  if (failedServers.length > maxItems) {
    lines.push(t('deletionJobs.syncWarningsMore', { count: failedServers.length - maxItems }))
  }

  return `${t('deletionJobs.syncWarningsTitle')}\n${lines.join('\n')}`
}

/**
 * @param {ReturnType<typeof import('@/stores/alert').useAlertStore>} alertStore
 */
export async function showAccountDeletionOutcome(alertStore, t, status, options = {}) {
  const jobId = status?.job_id
  if (jobId && isDeletionJobAlreadyHandled(jobId)) {
    return
  }
  if (jobId) {
    markDeletionJobHandled(jobId)
  }

  const {
    deleted,
    total,
    failedServers,
    message,
    syncWarnings,
  } = summarizeDeletionResult(status)

  const bulk = options.bulk !== false
  const preferToast = options.preferToast === true
  const noneKey = bulk ? 'viewMessages.accounts.bulkDeleteNone' : 'viewMessages.accounts.deleteError'
  const successKey = bulk ? 'viewMessages.accounts.bulkDeleteSuccess' : 'viewMessages.accounts.deleteSuccess'

  if (deleted <= 0) {
    await alertStore.error(t(noneKey))
    return
  }

  const mainMessage = message || t(successKey, { deleted, total, count: deleted })
  const hasSyncDetails = failedServers.length > 0 && deleted >= total

  if (hasSyncDetails) {
    const details = formatServerSyncDetails(failedServers, t)
    await alertStore.success(`${mainMessage}\n\n${details}`)
    return
  }

  if (syncWarnings) {
    await alertStore.warning(mainMessage)
    return
  }

  if (preferToast && !bulk) {
    alertStore.toastSuccess(mainMessage, { duration: 5000, saveToHistory: false })
    return
  }

  await alertStore.success(mainMessage)
}

export async function showResellerDeletionOutcome(alertStore, t, status, options = {}) {
  const jobId = status?.job_id
  if (jobId && isDeletionJobAlreadyHandled(jobId)) {
    return
  }
  if (jobId) {
    markDeletionJobHandled(jobId)
  }

  const {
    deleted,
    total,
    failedServers,
    message,
    syncWarnings,
  } = summarizeDeletionResult(status)

  const bulk = options.bulk !== false
  const preferToast = options.preferToast === true
  const noneKey = bulk ? 'viewMessages.resellers.bulkDeleteNone' : 'viewMessages.resellers.deleteError'
  const successKey = bulk ? 'viewMessages.resellers.bulkDeleteSuccess' : 'viewMessages.resellers.deleteSuccess'

  if (deleted <= 0) {
    await alertStore.error(t(noneKey))
    return
  }

  const mainMessage = message || t(successKey, { deleted, total, count: deleted })
  const hasSyncDetails = failedServers.length > 0 && deleted >= total

  if (hasSyncDetails) {
    const details = formatServerSyncDetails(failedServers, t)
    await alertStore.success(`${mainMessage}\n\n${details}`)
    return
  }

  if (syncWarnings) {
    await alertStore.warning(mainMessage)
    return
  }

  if (preferToast && !bulk) {
    alertStore.toastSuccess(mainMessage, { duration: 5000, saveToHistory: false })
    return
  }

  await alertStore.success(mainMessage)
}

export async function fetchDeletionJobStatus(apiService, jobId) {
  try {
    const res = await apiService.jobs.deletionStatus(jobId)
    return res?.data || null
  } catch (error) {
    if (error.response?.status === 404) {
      return null
    }
    throw error
  }
}
